import { useState } from "react";
import { useSnapshot } from "valtio";
import { analyticsStore } from "../stores/analytics";
import { useAnalyticsErrors } from "../api/analytics-api";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import type { AnalyticsEvent } from "@/lib/instantdb";
import {
  AlertCircle,
  AlertTriangle,
  ChevronDown,
  ChevronRight,
  Clock,
  Bug,
} from "lucide-react";

function formatTimestamp(ts: number): string {
  return new Date(ts).toLocaleString();
}

function formatRelativeTime(ts: number): string {
  const diff = Date.now() - ts;
  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);

  if (minutes < 60) return `${minutes}m ago`;
  if (hours < 24) return `${hours}h ago`;
  return `${days}d ago`;
}

interface ErrorItemProps {
  error: {
    id: string;
    eventName: string;
    errorMessage?: string;
    errorStack?: string;
    componentStack?: string;
    timestamp: number;
    routePath?: string;
  };
}

function ErrorItem({ error }: ErrorItemProps) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <div
        className="border rounded-lg mb-2"
        style={{ borderRadius: "var(--radius)" }}
      >
        <CollapsibleTrigger asChild>
          <button
            className="w-full p-4 flex items-start gap-3 text-left hover:bg-muted/50 transition-colors"
            style={{ borderRadius: "var(--radius)" }}
          >
            <div className="mt-0.5">
              {isOpen ? (
                <ChevronDown className="h-4 w-4 text-muted-foreground" />
              ) : (
                <ChevronRight className="h-4 w-4 text-muted-foreground" />
              )}
            </div>
            <AlertTriangle className="h-4 w-4 text-destructive mt-0.5 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-medium text-destructive">
                  {error.eventName}
                </span>
                {error.routePath && (
                  <Badge variant="outline" className="text-xs">
                    {error.routePath}
                  </Badge>
                )}
              </div>
              <p className="text-sm text-muted-foreground truncate mt-1">
                {error.errorMessage || "No message"}
              </p>
            </div>
            <div className="flex items-center gap-1 text-xs text-muted-foreground flex-shrink-0">
              <Clock className="h-3 w-3" />
              {formatRelativeTime(error.timestamp)}
            </div>
          </button>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div className="px-4 pb-4 pt-0 border-t">
            <div className="mt-3 space-y-3">
              <div>
                <div className="text-xs font-medium text-muted-foreground mb-1">
                  Error Message
                </div>
                <div className="text-sm bg-muted rounded p-2 break-words">
                  {error.errorMessage || "No message"}
                </div>
              </div>
              {error.errorStack && (
                <div>
                  <div className="text-xs font-medium text-muted-foreground mb-1">
                    Stack Trace
                  </div>
                  <pre className="text-xs bg-muted rounded p-2 overflow-x-auto whitespace-pre-wrap break-words max-h-48">
                    {error.errorStack}
                  </pre>
                </div>
              )}
              {error.componentStack && (
                <div>
                  <div className="text-xs font-medium text-muted-foreground mb-1">
                    Component Stack
                  </div>
                  <pre className="text-xs bg-muted rounded p-2 overflow-x-auto whitespace-pre-wrap break-words max-h-32">
                    {error.componentStack}
                  </pre>
                </div>
              )}
              <div className="text-xs text-muted-foreground">
                Occurred at: {formatTimestamp(error.timestamp)}
              </div>
            </div>
          </div>
        </CollapsibleContent>
      </div>
    </Collapsible>
  );
}

export default function AnalyticsErrors() {
  const { timeRange } = useSnapshot(analyticsStore);
  const { data, isLoading, error } = useAnalyticsErrors(timeRange);

  const errors = (data?.analytics_events ?? []) as AnalyticsEvent[];

  // Group errors by type
  const errorsByType = errors.reduce(
    (acc, err) => {
      const type = err.eventName || "Unknown";
      if (!acc[type]) {
        acc[type] = [];
      }
      acc[type].push(err);
      return acc;
    },
    {} as Record<string, AnalyticsEvent[]>,
  );

  const errorTypesSorted = Object.entries(errorsByType).sort(
    (a, b) => b[1].length - a[1].length,
  );

  return (
    <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
      {/* Error State */}
      {error && (
        <Alert variant="destructive" className="mb-6">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>{String(error)}</AlertDescription>
        </Alert>
      )}

      {/* Summary Cards */}
      <div className="grid gap-4 md:grid-cols-3 mb-8">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Total Errors
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-destructive">
              {errors.length}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Error Types
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{errorTypesSorted.length}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Most Common
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-lg font-medium truncate">
              {errorTypesSorted[0]?.[0] ?? "None"}
            </div>
            {errorTypesSorted[0] && (
              <div className="text-sm text-muted-foreground">
                {errorTypesSorted[0][1].length} occurrences
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Error Type Breakdown */}
      {errorTypesSorted.length > 0 && (
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Bug className="h-5 w-5" />
              Error Types
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {errorTypesSorted.map(([type, typeErrors]) => (
                <div
                  key={type}
                  className="flex items-center justify-between p-2 rounded bg-muted/50"
                >
                  <span className="font-medium">{type}</span>
                  <Badge variant="destructive">{typeErrors.length}</Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Error List */}
      <Card>
        <CardHeader>
          <CardTitle>Recent Errors</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-2">
              {[1, 2, 3, 4, 5].map((i) => (
                <Skeleton key={i} className="h-16 w-full" />
              ))}
            </div>
          ) : errors.length === 0 ? (
            <div className="text-center py-8">
              <AlertCircle className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">
                No errors found in the selected time range
              </p>
              <p className="text-sm text-muted-foreground mt-1">
                This is good news!
              </p>
            </div>
          ) : (
            <div>
              {errors.slice(0, 50).map((err) => (
                <ErrorItem key={err.id} error={err} />
              ))}
              {errors.length > 50 && (
                <div className="text-center py-4 text-sm text-muted-foreground">
                  Showing 50 of {errors.length} errors
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
